import { StatusDot, StatusPill } from "@/components/iris";
import type { StatusDotVariant, StatusPillVariant } from "@/components/iris";
import { derivePipelineHealth } from "@/features/overview/derive-health";
import { EnvKpiGrid } from "@/features/overview/env-kpi-grid";
import { useMultiEnvKpis } from "@/hooks/use-multi-env";
import { usePipelineStatus } from "@/hooks/use-pipeline";

interface Props {
  connected: boolean;
}

const OVERALL_VARIANT: Record<string, StatusDotVariant> = {
  healthy: "ok",
  warning: "warn",
  critical: "err",
  unknown: "idle",
};

const OVERALL_PILL: Record<string, StatusPillVariant> = {
  healthy: "ok",
  warning: "warn",
  critical: "err",
  unknown: "idle",
};

const OVERALL_LABEL: Record<string, string> = {
  healthy: "All Systems Healthy",
  warning: "Degraded",
  critical: "Incident Active",
  unknown: "Unknown",
};

const STAGE_VARIANT: Record<string, StatusDotVariant> = {
  healthy: "ok",
  warning: "warn",
  critical: "err",
  unknown: "idle",
};

export function HealthBanner({ connected }: Props) {
  const { data: pipeline } = usePipelineStatus();
  const { data: kpis } = useMultiEnvKpis();

  const overallStatus = derivePipelineHealth(pipeline?.stages);

  const prodKpis = kpis?.prod;
  const devKpis = kpis?.dev && !("error" in kpis.dev) ? kpis.dev : null;

  return (
    <>
      {/* IR-1034 QA3 (Rotem 45786 — "the overall health, separate it into its
          dedicated section, to be less confusing"): the shared-pipeline health
          status + its per-stage indicators now live in their OWN card, distinct
          from the Development / Production statistics frames below. */}
      <div
        className="iris-card"
        style={{
          padding: "var(--iris-s-5) var(--iris-s-5)",
          borderColor:
            overallStatus === "critical"
              ? "rgba(255, 77, 210, 0.25)"
              : overallStatus === "warning"
                ? "rgba(255, 153, 51, 0.25)"
                : undefined,
          background:
            overallStatus === "critical"
              ? "rgba(255, 77, 210, 0.07)"
              : overallStatus === "warning"
                ? "rgba(255, 153, 51, 0.07)"
                : undefined,
        }}
      >
        {/* Live indicator + status. IR-1034 QA (Rotem — "this Degraded Warning can't
            be common to both — not sure what it is"): label it explicitly as overall
            pipeline health (worst stage across SCRAPE→INDEX, shared by both
            environments) and add a tooltip, so it doesn't read as a per-env status. */}
        <div
          title="Overall pipeline health — the worst status across the SCRAPE → INGEST → TRANSLATE → ENRICH → INDEX stages shown below. It reflects the shared processing pipeline, not a single environment."
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <StatusDot
            variant={connected ? (OVERALL_VARIANT[overallStatus] ?? "ok") : "idle"}
            pulse={connected}
            size={10}
          />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--iris-fg-4)",
              }}
            >
              Pipeline health
            </span>
            <span
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "var(--iris-fg-1)",
              }}
            >
              {OVERALL_LABEL[overallStatus]}
            </span>
          </div>
          <StatusPill variant={OVERALL_PILL[overallStatus] ?? "ok"} style={{ marginLeft: 8 }}>
            {overallStatus.toUpperCase()}
          </StatusPill>
          <span className="iris-meta" style={{ marginLeft: "auto" }}>
            {connected ? "Live" : "Polling"}
          </span>
        </div>

        {/* Stage statuses — the per-stage breakdown of the shared pipeline,
            kept with the health status in this dedicated section. */}
        {pipeline && pipeline.stages.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
            {pipeline.stages.map((stage) => (
              <div
                key={stage.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: "var(--iris-r-md)",
                  border: "1px solid var(--iris-line-2)",
                  background: "rgba(255,255,255,0.03)",
                  padding: "6px 12px",
                }}
              >
                <StatusDot variant={STAGE_VARIANT[stage.status] ?? "idle"} size={8} />
                <span className="iris-meta">{stage.label}</span>
                <span
                  style={{
                    fontSize: "var(--iris-t-micro)",
                    fontWeight: 600,
                    color: "var(--iris-fg-1)",
                  }}
                >
                  {stage.throughput == null ? "—" : `${stage.throughput}${stage.unit}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* IR-1034 C: main statistics split into two titled frames (Development /
          Production), each showing the full per-system indicator set — mirrors
          the main-page design (EnvKpiGrid). Own card, separate from health (QA3). */}
      <div className="iris-card" style={{ padding: "var(--iris-s-5) var(--iris-s-5)" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--iris-s-5)",
          }}
        >
          <EnvKpiGrid env="dev" kpis={devKpis} />
          <EnvKpiGrid env="prod" kpis={prodKpis ?? null} />
        </div>
      </div>
    </>
  );
}
